import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sheet from '../../shared/ui/Sheet';
import { PrimaryButton } from '../../shared/ui';
import { formatDateTime, OrderStatus } from '../../shared/models';
import { fetchOrder } from '../../shared/services/orderService';
import { notificationMeta } from './notificationMeta';

// Mirrors iOS DriverNotificationDetailView as a bottom sheet.
// "View Order" routes to /history/:orderId when the linked order is a
// completed delivery, otherwise to the active-delivery detail page.
export default function NotificationDetailSheet({ notification, onClose }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const meta = notification ? notificationMeta(notification.type) : null;

  const openOrder = async () => {
    if (!notification?.orderId || busy) return;
    setBusy(true);
    try {
      const order = await fetchOrder(notification.orderId);
      onClose();
      if (order && [OrderStatus.DELIVERED, OrderStatus.COMPLETED].includes(order.status)) {
        navigate(`/driver/history/${order.id}`);
      } else {
        navigate(`/driver/delivery/${notification.orderId}`);
      }
    } catch (e) {
      console.error('openOrder', e);
      onClose();
      navigate(`/driver/history/${notification.orderId}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={!!notification} onClose={onClose} title="Driver Notification">
      {notification && (
        <div className="px-4 pb-6 space-y-5">
          <div className="flex items-center gap-4">
            {notification.type === 'promotion' || notification.data?.broadcast === 'true' ? (
              <img src="/ChefLogo.jpg" alt="CheF SKN" className="w-14 h-14 rounded-full object-cover shrink-0 bg-white" />
            ) : (
              <span className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 ${meta.cls}`}>
                <meta.Icon size={24} />
              </span>
            )}
            <div>
              <p className="text-xl font-bold text-gray-900">{notification.title || 'Notification'}</p>
              <p className="text-sm text-gray-400">{formatDateTime(notification.createdAt)}</p>
            </div>
          </div>

          <div className="space-y-3">
            {notification.data?.imageURL && (
              <button type="button" onClick={() => setShowFullImage(true)} className="relative block w-full text-left">
                <img src={notification.data.imageURL} alt="" className="w-full h-[220px] object-cover rounded-xl" />
                <span className="absolute bottom-2.5 right-2.5 bg-black/55 text-white text-[12px] font-semibold px-2.5 py-1 rounded-full">
                  Tap to view
                </span>
              </button>
            )}

            <h4 className="font-bold text-gray-900">Details</h4>
            <p className="text-sm text-gray-500 bg-gray-50 rounded-xl p-4 leading-relaxed">
              {notification.message}
            </p>

            <div className="flex flex-wrap gap-2">
              {notification.orderId && (
                <span className="text-[11px] font-medium text-blue-600 bg-blue-50 rounded-lg px-3 py-1.5">
                  Order ID: {notification.orderId}
                </span>
              )}
              {notification.restaurantId && (
                <span className="text-[11px] font-medium text-green-600 bg-green-50 rounded-lg px-3 py-1.5">
                  Restaurant ID: {notification.restaurantId}
                </span>
              )}
            </div>
          </div>

          {notification.orderId && (
            <PrimaryButton onClick={openOrder} disabled={busy}>
              {busy ? 'Opening…' : 'View Order'}
            </PrimaryButton>
          )}
        </div>
      )}
      {showFullImage && notification?.data?.imageURL && (
        <div
          className="fixed inset-0 z-[60] bg-black flex items-center justify-center"
          onClick={() => setShowFullImage(false)}
        >
          <img src={notification.data.imageURL} alt="" className="max-w-full max-h-full object-contain" />
          <button
            onClick={() => setShowFullImage(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center text-xl"
            aria-label="Close image"
          >
            ×
          </button>
        </div>
      )}
    </Sheet>
  );
}
